import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Fade, IconButton, Paper, Stack, Typography } from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { TagResultState, buildQrResultState } from '../../../Utils/scan/tag/scanTypes';
import { ScanWarningDialog } from '../ScanWarningDialog';
import { useCameraStore } from './cameraStore';

export function CameraWrapper({ children }: PropsWithChildren) {
    const tauri = isTauri();
    const { cameraOpen, cancel, errorMessage, isRenkiTag, redirectUrl, reset, scannedContent } = useCameraStore();
    const navigate = useNavigate();

    const handleClose = useCallback(() => {
        cancel();
        reset();
    }, [cancel, reset]);

    // Navigate when we have any terminal scan result (success or unsupported)
    useEffect(() => {
        if (!scannedContent || isRenkiTag == null) {
            return;
        }
        if (isRenkiTag && redirectUrl) {
            const resultState: TagResultState = buildQrResultState({ ok: true, raw: scannedContent, redirectUrl });
            navigate('/scan/result', { state: resultState });
            cancel();
        } else if (!isRenkiTag) {
            const resultState: TagResultState = buildQrResultState({
                error: errorMessage,
                ok: false,
                raw: scannedContent,
                redirectUrl: null,
            });
            navigate('/scan/result', { state: resultState });
            cancel();
        }
    }, [isRenkiTag, redirectUrl, scannedContent, navigate, cancel, errorMessage]);

    const [infoOpen, setInfoOpen] = useState(false);
    const closeInfo = useCallback(() => setInfoOpen(false), []);

    // Handle mobile / device back button (history back) to close scanner instead of navigating away
    const pushedHistoryRef = useRef(false);
    useEffect(() => {
        if (!tauri) {
            return;
        }
        if (cameraOpen && !pushedHistoryRef.current) {
            try {
                window.history.pushState({ scanner: true }, '');
                pushedHistoryRef.current = true;
            } catch (error) {
                console.warn('Failed to push history state for scanner', error);
            }
        }

        const handlePop = (ev: PopStateEvent) => {
            if (pushedHistoryRef.current && cameraOpen) {
                // Consume this back navigation to just close scanner
                ev.preventDefault();
                handleClose();
                // After closing, we don't re-push state; further back presses navigate normally
                pushedHistoryRef.current = false;
            }
        };

        window.addEventListener('popstate', handlePop);
        return () => {
            window.removeEventListener('popstate', handlePop);
            // If unmounting while state was pushed, we let user remain at current URL.
            pushedHistoryRef.current = false;
        };
    }, [cameraOpen, handleClose, tauri]);

    if (!tauri) {
        return children;
    }

    if (!cameraOpen) {
        return children;
    }

    return createPortal(
        <Box
            sx={{
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                height: '100dvh',
                inset: 0,
                paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
                paddingLeft: 'var(--safe-area-inset-left, 0px)',
                paddingRight: 'var(--safe-area-inset-right, 0px)',
                paddingTop: 'var(--safe-area-inset-top, 0px)',
                pointerEvents: 'none', // allow scanner surface to receive events except for controls
                position: 'fixed',
                width: '100dvw',
                zIndex: 1300,
            }}
        >
            <Stack
                spacing={2}
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                    position: 'relative',
                    pt: '40%',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        // Responsive sizing: use 320x350 when room, shrink on very small devices
                        alignItems: 'center',
                        border: '3px solid rgba(255,255,255,0.95)',
                        borderRadius: 2,
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
                        display: 'flex',
                        height: 'min(350px, 60vh)',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        width: 'min(320px, 90vw)',
                        // background: 'rgba(255,255,255,0.05)', // optional light overlay
                        backdropFilter: 'brightness(1.15) saturate(1.1)',
                    }}
                >
                    <Typography
                        sx={{
                            MozUserSelect: 'none',
                            pointerEvents: 'none',
                            position: 'absolute',
                            top: -32,
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                        }}
                        variant="body2"
                    >
                        Scan QR tag
                    </Typography>
                    <Fade in={Boolean(scannedContent)} mountOnEnter unmountOnExit>
                        <Paper
                            elevation={0}
                            sx={{
                                alignItems: 'center',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                justifyContent: 'center',
                                pointerEvents: 'auto',
                                px: 2,
                                py: 1.5,
                                zIndex: 1,
                                // background: 'rgba(30,30,30,0.55)',
                                backdropFilter: 'blur(10px) saturate(1.2)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                minWidth: 240,
                            }}
                        >
                            {isRenkiTag === true && redirectUrl ? (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <CheckCircleIcon color="success" fontSize="large" />
                                    <Typography sx={{ color: 'success.light' }} variant="h6">
                                        Redirect tag found
                                    </Typography>
                                </Stack>
                            ) : null}
                            {isRenkiTag === false && errorMessage ? (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <ErrorOutlinedIcon color="warning" fontSize="large" />
                                    <Typography
                                        sx={{
                                            color: 'error.main',
                                        }}
                                        variant="h6"
                                    >
                                        {String(errorMessage)}
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Paper>
                    </Fade>
                </Box>

                {/* Result actions removed – navigation handled automatically */}
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        color: '#000',
                        pointerEvents: 'auto',
                        position: 'absolute',
                        right: 25,
                        top: 0,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
            <ScanWarningDialog onClose={closeInfo} open={infoOpen} />
        </Box>,
        document.body,
    );
}
